import { ChangeEvent, ReactNode, useEffect, useRef } from 'react'

import { adjustLabelForOptionality } from './Option'
import { ProductOptionWithNameProps } from '../../../../../shared/js/Module'

const Input = ({
  details,
  disabled,
  id,
  fontClassName,
  label,
  name,
  onChange,
  required,
  triggerFocus,
  values: [value],
  valid,
}: ProductOptionWithNameProps) => {
  const inputId = `input-${id}`
  const descriptionId = details ? `description-input-${id}` : null
  const ariaDescribedBy = descriptionId
    ? { 'aria-describedby': descriptionId }
    : {}
  const fieldRef = useRef<HTMLInputElement>(null)

  const { fontPreview } = value

  // When autofocus is expected, trigger it,
  // then move smoothly into view
  useEffect(() => {
    if (fieldRef.current && triggerFocus) {
      // Do not scroll to field but to its parent otherwise
      // its label won't always be visible.
      fieldRef.current.parentElement?.scrollIntoView({ behavior: 'smooth' })
      fieldRef.current.focus({ preventScroll: true })
    }
  }, [triggerFocus, fontClassName])

  let finalLabel: ReactNode = label
  if (!required) {
    finalLabel = adjustLabelForOptionality(label)
  }

  return (
    <div className='u-mb-2'>
      <label
        className='c-label c-label--image'
        htmlFor={inputId}
        {...ariaDescribedBy}
      >
        {finalLabel}
      </label>
      <input
        aria-required={required}
        aria-invalid={!valid}
        className={fontPreview && fontClassName ? fontClassName : undefined}
        defaultValue={value?.text || ''}
        disabled={disabled}
        id={inputId}
        name={name}
        onChange={handleChange}
        ref={fieldRef}
        type='text'
      />
      {!valid && (
        <p className='c-message c-message--error'>
          Vous devez renseigner un texte
        </p>
      )}
      {descriptionId && (
        <p id={descriptionId} className='c-message c-message--help'>
          {details}
        </p>
      )}
    </div>
  )

  function handleChange({
    target: { value: text },
  }: ChangeEvent<HTMLInputElement>) {
    onChange?.(id, [{ ...value, text: text.trim(), selected: text.length > 0 }])
  }
}

export default Input
